<template>
  <div class="credits-slider-card" :class="{ 'no-select': isDisabled }" @copy="handleCopy">
    <div class="font-semibold text-[20px] text_color mb-1">Credits</div>
    <div class="text-sm text-[#888] mb-6">Credits are shared across users. Select how many credits you would like for your team below.</div>

    <div v-if="props.loading" class="flex justify-center items-center py-8">
      <div class="text-sm text-[#888]">Loading credit packs...</div>
    </div>
    <div v-else-if="props.creditPacks.length > 0" class="flex flex-col gap-4 relative">
      <div class="flex flex-row justify-between items-center mb-4">
        <div class="text-[20px] font-semibold text_color transition-all duration-300 ease-out">
          <span v-if="sliderValue !== -1"> {{ displayValue }} credits/{{ getBillingPeriodFrequencyText(billingPeriod) }} </span>
          <span v-else> {{ displayValue }} </span>
        </div>
        <div class="text-right">
          <div v-if="selectedPackPrice !== 'Talk to Sales'" class="text-[24px] font-bold text_color transition-all duration-300 ease-out">
            ${{ selectedPackPrice.toLocaleString() }}<span class="text-sm secondary_color">/{{ getBillingPeriodFrequencyText(billingPeriod) }}</span>
          </div>
          <div v-else class="text-[24px] font-bold text_color transition-all duration-300 ease-out">
            {{ selectedPackPrice }}
          </div>
        </div>
      </div>
      <div class="slider-row">
        <div class="slider-track-wrapper">
          <div class="slider-bar-bg"></div>
          <div class="slider-bar-fill" :style="{ width: fillPercent + '%' }"></div>
          <input type="range" :min="0" :max="props.creditPacks.length - 1" :step="1" :value="sliderIndex" class="credits-slider" @input="handleSliderInput" :disabled="isDisabled" />
          <span class="slider-thumb" :style="thumbStyle">
            <ThemeImage :icon="imageNames.DRAG_ICON_GREY" alt="drag_icon" width="18" height="18" />
          </span>
        </div>
        <div class="slider-labels">
          <span
            v-for="(pack, index) in props.creditPacks"
            :key="pack.pack_size"
            :class="['slider-mark', sliderIndex === index ? 'active' : '']"
            :style="{ position: 'absolute', left: `${(index / (props.creditPacks.length - 1)) * 100}%`, transform: 'translateX(-50%)' }"
          >
            {{ formatSliderLabel(pack.pack_size) }}
          </span>
        </div>
      </div>

      <div v-if="isDisabled" class="disabled-overlay">
        <div class="lock-icon">
          <ThemeImage :icon="imageNames.LOCK_ICON" alt="lock" width="48" height="48" />
        </div>
        <div class="disabled-text">
          <div class="text-[14px] font-semibold mb-1">Select a paid plan to add credits</div>
          <div class="secondary_color text-[13px]">Credits will be available on paid plans only. Please select a paid plan to add additional credits</div>
        </div>
      </div>
    </div>
    <div v-else class="flex justify-center items-center py-8">
      <div class="text-sm text-[#888]">No credit packs available</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import ThemeImage from '@/components/custom/ThemeImage.vue';
import { imageNames } from '@/javascript/imageConstants';
import { getBillingPeriodSuffix, getBillingPeriodFrequencyText } from '@/utils/billingPeriodUtils';

const props = defineProps({
  billingPeriod: {
    type: String,
    default: 'monthly',
  },
  currentSelectedPack: {
    type: Number,
    default: 0,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  creditPacks: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['pack-selected']);

const sliderValue = ref(0);
const sliderIndex = ref(0);

// Computed properties
const displayValue = computed(() => {
  if (sliderValue.value === -1) return 'Custom Credits';
  return formatPackSize(sliderValue.value);
});

const selectedPackPrice = computed(() => {
  const pack = props.creditPacks[sliderIndex.value];
  if (pack && pack.isCustom) return 'Talk to Sales';
  return pack ? pack.pack_price : 0;
});

const fillPercent = computed(() => {
  if (props.creditPacks.length <= 1) return 0;
  return (sliderIndex.value / (props.creditPacks.length - 1)) * 100;
});

const thumbStyle = computed(() => {
  return {
    left: `calc(${fillPercent.value}% - 6px)`,
  };
});

const isDisabled = computed(() => {
  // return props.selectedPlanId === 'free';
  return false;
});

const handleCopy = (e) => {
  if (isDisabled.value) {
    e.preventDefault();
  }
};

// Helper functions
function formatPackSize(size) {
  if (size === -1) return 'Custom';
  if (size === 0) return '0';
  if (size >= 1000000) return `${size / 1000000}m`;
  if (size >= 1000) return `${size / 1000}K`;
  return size.toString();
}

function formatSliderLabel(size) {
  if (size === -1) return 'Custom';
  if (size === 0) return '0';
  if (size >= 1000000) return `${(size / 1000000).toFixed(1)}M`;
  if (size >= 1000) {
    const suffix = getBillingPeriodSuffix(props.billingPeriod);
    const value = size / 1000;
    return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}K${suffix}`;
  }
  const suffix = getBillingPeriodSuffix(props.billingPeriod);
  return `${size}${suffix}`;
}

const updateSliderValue = async () => {
  if (!props.creditPacks || props.creditPacks.length === 0) {
    sliderIndex.value = 0;
    sliderValue.value = 0;
    return;
  }

  // Find the index of the pack that matches currentSelectedPack
  if (props.currentSelectedPack > 0) {
    const matchingIndex = props.creditPacks.findIndex((pack) => pack.pack_size === props.currentSelectedPack);
    if (matchingIndex !== -1) {
      console.log(`CreditsSliderV3: Found matching pack at index ${matchingIndex} for pack_size ${props.currentSelectedPack}`);
      sliderIndex.value = matchingIndex;
      sliderValue.value = props.currentSelectedPack;
    } else {
      console.log(`CreditsSliderV3: No matching pack found for pack_size ${props.currentSelectedPack}, defaulting to first pack`);
      // Default to first pack if no match found
      sliderIndex.value = 0;
      sliderValue.value = props.creditPacks[0]?.pack_size || 0;
    }
  } else {
    // Default to first pack
    sliderIndex.value = 0;
    sliderValue.value = props.creditPacks[0]?.pack_size || 0;
  }
  emit('pack-selected', sliderValue.value);
};

const handleSliderInput = async (event) => {
  if (isDisabled.value) return;

  const newIndex = parseInt(event.target.value);
  sliderIndex.value = newIndex;

  if (props.creditPacks[newIndex]) {
    sliderValue.value = props.creditPacks[newIndex].pack_size;

    const pack = props.creditPacks[newIndex];
    if (pack.pack_size === 0) {
      emit('pack-selected', 0);
    } else {
      emit('pack-selected', pack.pack_size);
    }
  }
};

// Watchers
watch(
  () => [props.billingPeriod, props.creditPacks, props.currentSelectedPack],
  async () => {
    await updateSliderValue();
  },
  { deep: true, immediate: true }
);

// Lifecycle - removed onMounted since watcher with immediate: true handles initialization
</script>

<style scoped>
.credits-slider-card {
  background: var(--white-opacity-4);
  border: 1px solid var(--white-opacity-8);
  border-radius: 16px;
  padding: 32px 32px 24px 32px;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  position: relative;
  isolation: isolate;
}

.disabled-overlay {
  position: absolute;
  top: 0;
  left: -32px;
  right: -32px;
  bottom: -24px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 16px;
  overflow: hidden;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  pointer-events: none;
}

.no-select .disabled-overlay,
.no-select .disabled-overlay * {
  pointer-events: auto;
}

.lock-icon {
  margin-bottom: 8px;
  opacity: 0.7;
}

.disabled-text {
  text-align: center;
  max-width: 380px;
}

.disabled-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
}

.slider-row {
  width: 100%;
}
.slider-track-wrapper {
  position: relative;
  width: 100%;
  height: 22px;
  display: flex;
  align-items: center;
}
.slider-bar-bg {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 2px;
  background: #e0e0e0;
  border-radius: 1px;
  transform: translateY(-50%);
  z-index: 1;
}
.slider-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 22px;
  background: linear-gradient(90deg, #7095eb 0%, #b343cb 100%);
  border-radius: 32px 6px 6px 32px;
  z-index: 2;
  transition: width 0.15s ease-out;
}
.credits-slider {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 22px;
  opacity: 0;
  z-index: 4;
  margin: 0;
  cursor: pointer;
}
.credits-slider:disabled {
  cursor: not-allowed;
}
.credits-slider:not(:disabled):hover ~ .slider-thumb {
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
}
.slider-thumb {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 22px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  pointer-events: none;
  transition:
    left 0.15s ease-out,
    transform 0.15s ease-out,
    box-shadow 0.15s ease-out;
}
.slider-labels {
  width: 100%;
  font-size: 15px;
  color: #888;
  user-select: none;
  position: relative;
  height: 20px;
  margin-top: 8px;
}
.slider-mark {
  min-width: 32px;
  text-align: center;
  font-weight: 500;
  transition: color 0.2s;
  white-space: nowrap;
}
.slider-mark.active {
  color: #222;
  font-weight: bold;
}

.no-select,
.no-select * {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
</style>
